import { DatePipe } from '@angular/common';
import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
    untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { SelectButton } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Tag } from 'primeng/tag';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import {
    AdminGame,
    FakeReviewAuthor,
    GameReview,
    GameReviewKindFilter,
} from '@/lib-backend';
import { FakeReviewService } from '@/app/services/fake-review.service';
import { effectiveTitle } from '@/app/utils/game';
import { GenerateReviewsPanel } from './generate-reviews-panel';

const FETCH_CHUNK = 500;
const RATING_MIN = 1;
const RATING_MAX = 5;

interface EditForm {
    Rating: number;
    Comment: string;
    CreatedAt: Date;
}

interface AddForm {
    AuthorId: string | null;
    Rating: number;
    Comment: string;
    CreatedAt: Date;
}

@Component({
    selector: 'app-review-moderation-dialog',
    standalone: true,
    imports: [
        DatePipe,
        FormsModule,
        Button,
        DatePicker,
        Dialog,
        Message,
        Select,
        SelectButton,
        TableModule,
        Tab,
        TabList,
        TabPanel,
        TabPanels,
        Tabs,
        Tag,
        Textarea,
        Toast,
        Tooltip,
        GenerateReviewsPanel,
    ],
    providers: [MessageService],
    templateUrl: './review-moderation-dialog.html',
})
export class ReviewModerationDialog {
    @Input({ required: true }) set visible(v: boolean) {
        this.visibleSignal.set(v);
    }

    @Input() set adminGame(g: AdminGame | null) {
        this.gameSignal.set(g);
    }

    @Output() visibleChange = new EventEmitter<boolean>();

    private readonly fakeReviewService = inject(FakeReviewService);
    private readonly messageService = inject(MessageService);

    protected readonly visibleSignal = signal(false);
    protected readonly gameSignal = signal<AdminGame | null>(null);

    // ── List ────────────────────────────────────────────────────────────────
    protected readonly reviews = signal<GameReview[]>([]);
    protected readonly loading = signal(false);
    protected readonly listError = signal<string | null>(null);

    // ── Filters ─────────────────────────────────────────────────────────────
    protected readonly kindFilter = signal<GameReviewKindFilter>(
        GameReviewKindFilter.All,
    );
    /** null = any rating. */
    protected readonly ratingFilter = signal<number | null>(null);

    protected readonly kindOptions = [
        { label: 'All', value: GameReviewKindFilter.All },
        { label: 'Real', value: GameReviewKindFilter.RealOnly },
        { label: 'Fake', value: GameReviewKindFilter.FakeOnly },
    ];

    protected readonly ratingPickOptions = [
        { label: '5★', value: 5 },
        { label: '4★', value: 4 },
        { label: '3★', value: 3 },
        { label: '2★', value: 2 },
        { label: '1★', value: 1 },
    ];

    protected readonly ratingOptions = [
        { label: 'All ratings', value: null },
        ...this.ratingPickOptions,
    ];

    /** Active fake authors, used for the add form and to resolve fake usernames. */
    protected readonly authors = signal<FakeReviewAuthor[]>([]);
    private readonly authorNameById = computed(() => {
        const map = new Map<string, string>();
        for (const a of this.authors()) map.set(a.Id, a.Username);
        return map;
    });

    /**
     * Author ids already used by a fake review on this game. One author may have
     * at most one review per game (backend unique index), so these are excluded
     * from the add form to avoid an AuthorAlreadyUsed rejection.
     */
    private readonly usedFakeAuthorIds = computed(() => {
        const ids = new Set<string>();
        for (const r of this.reviews()) {
            if (r.IsFake) ids.add(r.UserId);
        }
        return ids;
    });

    /** Active fake authors not yet used on this game — selectable in the add form. */
    protected readonly availableAuthors = computed(() => {
        const used = this.usedFakeAuthorIds();
        return this.authors().filter((a) => !used.has(a.Id));
    });

    protected readonly filteredReviews = computed(() => {
        const rating = this.ratingFilter();
        const list = this.reviews();
        if (rating === null) return list;
        return list.filter((r) => r.Rating === rating);
    });

    protected readonly fakeCount = computed(
        () => this.reviews().filter((r) => r.IsFake).length,
    );
    protected readonly realCount = computed(
        () => this.reviews().filter((r) => !r.IsFake).length,
    );

    // ── Edit dialog (fakes only) ────────────────────────────────────────────
    protected readonly editReview = signal<GameReview | null>(null);
    protected readonly editForm = signal<EditForm | null>(null);
    protected readonly editSaving = signal(false);
    protected readonly editError = signal<string | null>(null);

    // ── Add dialog ──────────────────────────────────────────────────────────
    protected readonly addVisible = signal(false);
    protected readonly addForm = signal<AddForm | null>(null);
    protected readonly addSaving = signal(false);
    protected readonly addError = signal<string | null>(null);

    // ── Delete confirm ──────────────────────────────────────────────────────
    protected readonly deleteReview = signal<GameReview | null>(null);
    protected readonly deleting = signal(false);

    // ── Delete-all-fakes confirm ────────────────────────────────────────────
    protected readonly removeAllConfirmVisible = signal(false);
    protected readonly removeAllBusy = signal(false);

    // ── Tabs ──────────────────────────────────────────────────────────────────
    protected readonly activeTab = signal<'moderate' | 'generate'>('moderate');

    protected readonly ratingMin = RATING_MIN;
    protected readonly ratingMax = RATING_MAX;
    protected readonly editFilter = GameReviewKindFilter;
    protected readonly effectiveTitle = effectiveTitle;

    constructor() {
        effect(() => {
            const visible = this.visibleSignal();
            const g = this.gameSignal();
            if (visible && g) {
                untracked(() => {
                    this.activeTab.set('moderate');
                    this.ratingFilter.set(null);
                    this.kindFilter.set(GameReviewKindFilter.All);
                    this.reload();
                    this.reloadAuthors();
                });
            }
        });
    }

    protected onTabChange(value: string | number | undefined): void {
        this.activeTab.set(value === 'generate' ? 'generate' : 'moderate');
    }

    protected onVisibleChange(value: boolean): void {
        this.visibleSignal.set(value);
        this.visibleChange.emit(value);
        if (!value) this.clear();
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    private clear(): void {
        this.reviews.set([]);
        this.listError.set(null);
        this.editReview.set(null);
        this.editForm.set(null);
        this.addVisible.set(false);
        this.addForm.set(null);
        this.deleteReview.set(null);
    }

    // ── Loaders ─────────────────────────────────────────────────────────────

    protected onKindFilterChange(value: GameReviewKindFilter): void {
        this.kindFilter.set(value ?? GameReviewKindFilter.All);
        this.reload();
    }

    protected async reload(): Promise<void> {
        const g = this.gameSignal();
        if (!g || this.loading()) return;
        this.loading.set(true);
        this.listError.set(null);
        const collected: GameReview[] = [];
        try {
            let skip = 0;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const outcome = await this.fakeReviewService.listGameReviews(
                    g.RealmId,
                    g.Id,
                    this.kindFilter(),
                    skip,
                    FETCH_CHUNK,
                );
                if (outcome.result !== 'Ok') {
                    this.listError.set('Failed to load reviews.');
                    return;
                }
                collected.push(...outcome.reviews);
                if (outcome.reviews.length < FETCH_CHUNK) break;
                skip += FETCH_CHUNK;
            }
            this.reviews.set(collected);
        } finally {
            this.loading.set(false);
        }
    }

    private async reloadAuthors(): Promise<void> {
        const collected: FakeReviewAuthor[] = [];
        let skip = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const outcome = await this.fakeReviewService.listAuthors(
                true,
                skip,
                FETCH_CHUNK,
            );
            if (outcome.result !== 'Ok') return;
            collected.push(...outcome.authors);
            if (outcome.authors.length < FETCH_CHUNK) break;
            skip += FETCH_CHUNK;
        }
        this.authors.set(collected);
    }

    protected authorLabel(review: GameReview): string {
        if (review.IsFake) {
            const name = this.authorNameById().get(review.UserId);
            if (name) return name;
        }
        return this.shortId(review.UserId);
    }

    private shortId(id: string): string {
        return id.length > 10 ? `${id.slice(0, 8)}…` : id;
    }

    // ── Edit (fakes only) ───────────────────────────────────────────────────

    protected openEdit(review: GameReview): void {
        if (!review.IsFake) return;
        this.editReview.set(review);
        this.editForm.set({
            Rating: review.Rating,
            Comment: review.Comment,
            CreatedAt: new Date(review.CreatedAt),
        });
        this.editError.set(null);
    }

    protected closeEdit(): void {
        this.editReview.set(null);
        this.editForm.set(null);
        this.editError.set(null);
    }

    protected updateEditRating(value: number): void {
        this.editForm.update((f) => (f ? { ...f, Rating: value } : f));
    }

    protected updateEditComment(value: string): void {
        this.editForm.update((f) => (f ? { ...f, Comment: value } : f));
    }

    protected updateEditCreatedAt(value: Date | null): void {
        this.editForm.update((f) =>
            f ? { ...f, CreatedAt: value ?? f.CreatedAt } : f,
        );
    }

    protected async saveEdit(): Promise<void> {
        const orig = this.editReview();
        const form = this.editForm();
        if (!orig || !form) return;

        const comment = form.Comment.trim();
        if (comment.length === 0) {
            this.editError.set("Comment can't be empty.");
            return;
        }
        if (form.Rating < RATING_MIN || form.Rating > RATING_MAX) {
            this.editError.set(
                `Rating must be in [${RATING_MIN}..${RATING_MAX}].`,
            );
            return;
        }

        const createdAtIso = form.CreatedAt.toISOString();
        const patch = {
            UserId: null,
            Rating: form.Rating !== orig.Rating ? form.Rating : null,
            Comment: comment !== orig.Comment ? comment : null,
            CreatedAt: createdAtIso !== orig.CreatedAt ? createdAtIso : null,
        };

        if (
            patch.Rating === null &&
            patch.Comment === null &&
            patch.CreatedAt === null
        ) {
            this.editError.set('No changes.');
            return;
        }

        this.editSaving.set(true);
        this.editError.set(null);
        try {
            const outcome = await this.fakeReviewService.updateFakeGameReview(
                orig.RealmId,
                orig.Id,
                patch,
            );
            if (outcome.result === 'Ok') {
                this.applyUpdate(outcome.review);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Review updated',
                });
                this.closeEdit();
                return;
            }
            this.editError.set(this.updateErrorText(outcome.result));
        } finally {
            this.editSaving.set(false);
        }
    }

    private updateErrorText(
        result:
            | 'NotFound'
            | 'WrongType'
            | 'InvalidAuthor'
            | 'AuthorAlreadyUsed'
            | 'Error',
    ): string {
        switch (result) {
            case 'NotFound':
                return 'Review not found — it may have been removed.';
            case 'WrongType':
                return 'This review is not a fake and cannot be edited.';
            case 'InvalidAuthor':
                return 'Selected author is invalid.';
            case 'AuthorAlreadyUsed':
                return 'This author already has a review for this game.';
            default:
                return 'Failed to update review.';
        }
    }

    // ── Add fake review ─────────────────────────────────────────────────────

    protected openAdd(): void {
        this.addForm.set({
            AuthorId: null,
            Rating: 5,
            Comment: '',
            CreatedAt: new Date(),
        });
        this.addError.set(null);
        this.addVisible.set(true);
    }

    protected closeAdd(): void {
        this.addVisible.set(false);
        this.addForm.set(null);
        this.addError.set(null);
    }

    protected updateAddAuthor(value: string | null): void {
        this.addForm.update((f) => (f ? { ...f, AuthorId: value } : f));
    }

    protected updateAddRating(value: number): void {
        this.addForm.update((f) => (f ? { ...f, Rating: value } : f));
    }

    protected updateAddComment(value: string): void {
        this.addForm.update((f) => (f ? { ...f, Comment: value } : f));
    }

    protected updateAddCreatedAt(value: Date | null): void {
        this.addForm.update((f) =>
            f ? { ...f, CreatedAt: value ?? f.CreatedAt } : f,
        );
    }

    protected async saveAdd(): Promise<void> {
        const g = this.gameSignal();
        const form = this.addForm();
        if (!g || !form) return;

        if (!form.AuthorId) {
            this.addError.set('Select a fake author.');
            return;
        }
        const comment = form.Comment.trim();
        if (comment.length === 0) {
            this.addError.set("Comment can't be empty.");
            return;
        }
        if (form.Rating < RATING_MIN || form.Rating > RATING_MAX) {
            this.addError.set(
                `Rating must be in [${RATING_MIN}..${RATING_MAX}].`,
            );
            return;
        }

        this.addSaving.set(true);
        this.addError.set(null);
        try {
            const outcome = await this.fakeReviewService.addFakeGameReview({
                realmId: g.RealmId,
                gameId: g.Id,
                userId: form.AuthorId,
                rating: form.Rating,
                comment,
                createdAt: form.CreatedAt.toISOString(),
            });
            if (outcome.result === 'Ok') {
                this.insertReview(outcome.review);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Fake review added',
                });
                this.closeAdd();
                return;
            }
            if (outcome.result === 'InvalidAuthor') {
                this.addError.set('Selected author is invalid.');
            } else if (outcome.result === 'AuthorAlreadyUsed') {
                this.addError.set(
                    'This author already has a review for this game.',
                );
            } else {
                this.addError.set('Failed to add review.');
            }
        } finally {
            this.addSaving.set(false);
        }
    }

    // ── Delete (any) ────────────────────────────────────────────────────────

    protected requestDelete(review: GameReview): void {
        this.deleteReview.set(review);
    }

    protected cancelDelete(): void {
        this.deleteReview.set(null);
    }

    protected async confirmDelete(): Promise<void> {
        const review = this.deleteReview();
        if (!review) return;
        this.deleting.set(true);
        try {
            const outcome = review.IsFake
                ? await this.fakeReviewService.deleteFakeGameReview(
                      review.RealmId,
                      review.Id,
                  )
                : await this.fakeReviewService.deleteRealGameReview(
                      review.RealmId,
                      review.Id,
                  );
            if (outcome.result === 'Ok') {
                this.removeLocal(review.Id);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Review deleted',
                });
            } else if (outcome.result === 'NotFound') {
                this.removeLocal(review.Id);
                this.messageService.add({
                    severity: 'info',
                    summary: 'Review already gone',
                });
            } else if (outcome.result === 'WrongType') {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Wrong review type',
                    detail: 'The review kind changed — refresh and retry.',
                });
            }
        } finally {
            this.deleting.set(false);
            this.deleteReview.set(null);
        }
    }

    // ── Delete all fakes ────────────────────────────────────────────────────

    protected requestRemoveAll(): void {
        if (this.fakeCount() === 0) return;
        this.removeAllConfirmVisible.set(true);
    }

    protected cancelRemoveAll(): void {
        this.removeAllConfirmVisible.set(false);
    }

    protected async confirmRemoveAll(): Promise<void> {
        const g = this.gameSignal();
        if (!g) return;
        this.removeAllConfirmVisible.set(false);
        this.removeAllBusy.set(true);
        try {
            const outcome = await this.fakeReviewService.removeAllGameReviews(
                g.RealmId,
                g.Id,
            );
            if (outcome.result === 'Ok') {
                this.messageService.add({
                    severity: 'success',
                    summary: 'All fake reviews removed',
                    detail: `Removed ${outcome.removedCount}.`,
                });
                this.reload();
            }
        } finally {
            this.removeAllBusy.set(false);
        }
    }

    // ── Local state helpers ─────────────────────────────────────────────────

    private applyUpdate(updated: GameReview): void {
        this.reviews.update((list) =>
            list.map((r) => (r.Id === updated.Id ? updated : r)),
        );
    }

    private insertReview(review: GameReview): void {
        // Respect the active author-kind filter: a fake review only belongs
        // to the loaded set when fakes are shown.
        const kind = this.kindFilter();
        if (kind === GameReviewKindFilter.RealOnly) return;
        this.reviews.update((list) => [review, ...list]);
    }

    private removeLocal(id: string): void {
        this.reviews.update((list) => list.filter((r) => r.Id !== id));
    }

    protected trackById(_index: number, review: GameReview): string {
        return review.Id;
    }
}
